<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, ref, watch } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import AiAssistantAPI from 'dashboard/api/aiAssistant';

const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// the AI assistant panel is offered whenever the inbox runs in suggestion mode
const aiAssistantMode = ref('disabled');
// suggestion mode offers the draft panel; auto mode offers the audit trail
const showAiTab = computed(() => aiAssistantMode.value !== 'disabled');

const currentChat = useMapGetter('getSelectedChat');

const fetchAiStatus = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    aiAssistantMode.value = 'disabled';
    return;
  }
  try {
    const { data } = await AiAssistantAPI.status(conversationId);
    aiAssistantMode.value = data.mode || 'disabled';
  } catch (error) {
    aiAssistantMode.value = 'disabled';
  }
};

watch(() => currentChat.value?.id, fetchAiStatus, { immediate: true });

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isAiPanelOpen = computed(() => uiSettings.value.is_ai_panel_open);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_ai_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_ai_panel_open: false,
  });
};

const handleAiSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_ai_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow !z-20"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showAiTab"
      v-tooltip.bottom="$t('AI_ASSISTANT.PANEL.TITLE')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isAiPanelOpen,
      }"
      icon="i-lucide-sparkles"
      @click="handleAiSidebarToggle"
    />
  </ButtonGroup>
</template>
